import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  listAdminLeads,
  listTenants,
  type AdminLeadFilters,
  type AdminLeadSummary,
  type TenantSummary,
} from "@/lib/api";
import {
  Button,
  Card,
  ErrorBanner,
  Field,
  PageContainer,
  Select,
  TextInput,
  toErrorMessage,
} from "@/components/Forms";

const ENGINE_STATES = [
  "IDLE",
  "INTAKE",
  "AWAITING_FIRST_REPLY",
  "QUALIFYING",
  "MEETING_PROPOSED",
  "BOOKED",
  "PRE_MEETING_NURTURE",
  "POST_MEETING",
  "WON",
  "DORMANT",
  "REACTIVATING_30",
  "REACTIVATING_60",
  "REACTIVATING_90",
  "DEAD_DISQUALIFIED",
  "DEAD_NO_RESPONSE",
  "DEAD_INVALID_CONTACT",
  "DEAD_OPTOUT",
  "ESCALATED_HUMAN",
];

export default function AdminLeadsPage() {
  const { t } = useTranslation();
  const [tenants, setTenants] = useState<TenantSummary[]>([]);
  const [filters, setFilters] = useState<AdminLeadFilters>({
    tenant: "",
    limit: 100,
  });
  const [rows, setRows] = useState<AdminLeadSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    listTenants()
      .then((ts) => {
        setTenants(ts);
        if (ts.length > 0 && !filters.tenant) {
          setFilters((f) => ({ ...f, tenant: ts[0].slug }));
        }
      })
      .catch(() => {
        // Silent — empty tenant list will show in select.
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function search() {
    if (!filters.tenant) {
      setError(t("leads.filterErrorTenantRequired"));
      return;
    }
    setError(null);
    setSearched(true);
    setRows(null);
    try {
      const res = await listAdminLeads(filters);
      setRows(res.leads);
    } catch (e) {
      setError(toErrorMessage(e, t("leads.loadError")));
      setRows([]);
    }
  }

  return (
    <PageContainer
      title={t("leads.title")}
      subtitle={t("leads.subtitle")}
    >
      <ErrorBanner message={error} />

      <Card title={t("leads.search")}>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <Field label={t("leads.filterTenant")} required>
            <Select value={filters.tenant} onChange={(e) => setFilters({ ...filters, tenant: e.target.value })}>
              <option value="">{t("leads.filterClaimAny")}</option>
              {tenants.map((tt) => (
                <option key={tt.slug} value={tt.slug}>
                  {tt.slug} · {tt.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t("leads.filterState")}>
            <Select
              value={filters.engineState ?? ""}
              onChange={(e) => setFilters({ ...filters, engineState: e.target.value || undefined })}
            >
              <option value="">{t("leads.filterClaimAny")}</option>
              {ENGINE_STATES.map((s) => (
                <option key={s} value={s}>
                  {t(`leadEnum.state_${s}`, { defaultValue: s })}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t("leads.filterClaim")}>
            <Select
              value={filters.claimStatus ?? ""}
              onChange={(e) => setFilters({ ...filters, claimStatus: (e.target.value || undefined) as AdminLeadFilters["claimStatus"] })}
            >
              <option value="">{t("leads.filterClaimAny")}</option>
              <option value="claimed">{t("leads.filterClaimClaimed")}</option>
              <option value="unclaimed">{t("leads.filterClaimUnclaimed")}</option>
              <option value="stuck">{t("leads.filterClaimStuck")}</option>
            </Select>
          </Field>
          <Field label={t("leads.filterSince")}>
            <TextInput
              value={filters.since ?? ""}
              onChange={(e) => setFilters({ ...filters, since: e.target.value || undefined })}
              placeholder="2026-05-01T00:00:00Z"
            />
          </Field>
          <Field label={t("leads.filterLimit")}>
            <TextInput
              type="number"
              value={filters.limit ?? 100}
              onChange={(e) => setFilters({ ...filters, limit: Number(e.target.value) || 100 })}
            />
          </Field>
        </div>
        <div className="mt-3">
          <Button variant="primary" onClick={search}>
            {t("leads.search")}
          </Button>
        </div>
      </Card>

      <div className="h-3" />

      {searched && rows !== null && (
        <Card title={`${t("leads.search")} (${rows.length})`}>
          {rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("leads.empty")}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="text-left px-2 py-2">id</th>
                    <th className="text-left px-2 py-2">name / phone</th>
                    <th className="text-left px-2 py-2">state</th>
                    <th className="text-left px-2 py-2">claimed</th>
                    <th className="text-left px-2 py-2">next action</th>
                    <th className="text-left px-2 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {rows.map((l) => (
                    <tr key={l.id} className="hover:bg-muted/30">
                      <td className="px-2 py-2 font-mono text-xs">{l.id.slice(0, 8)}…</td>
                      <td className="px-2 py-2">
                        <div className="truncate max-w-[14rem]">{l.name ?? "—"}</div>
                        <div className="text-xs text-muted-foreground font-mono">{l.phone ?? "—"}</div>
                      </td>
                      <td className="px-2 py-2">
                        <span className="rounded-sm bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide">
                          {l.engineState ? t(`leadEnum.state_${l.engineState}`, { defaultValue: l.engineState }) : "—"}
                        </span>
                        {!l.engineEnabled && (
                          <span className="ml-1 rounded-sm bg-amber-500/15 text-amber-400 px-1.5 py-0.5 text-[10px] uppercase">
                            {t("leads.paused")}
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-2 text-xs font-mono">
                        {l.claimedAt ? (
                          <span className="text-amber-400" title={`by ${l.claimedBy}`}>{l.claimedAt.slice(11, 19)}</span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-2 py-2 text-xs font-mono text-muted-foreground">
                        {l.engineNextActionAt ?? "—"}
                      </td>
                      <td className="px-2 py-2 text-right">
                        <Link
                          to={`/leads/${encodeURIComponent(filters.tenant)}/${encodeURIComponent(l.id)}`}
                          className="rounded-md border border-border bg-background px-2.5 py-1 text-xs hover:bg-muted"
                        >
                          →
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}
    </PageContainer>
  );
}
